/** @jsxImportSource @emotion/react */

import React from 'react';
import CharacterHome from '@component/iyagigo/classroom/character-home';
import ClassroomExpressionHome from '@component/iyagigo/classroom/classroom-expression-home';
import ClassroomConversationHome from '@component/iyagigo/classroom/classroom-conversation-home';
import GrammarHome from '@component/iyagigo/classroom/grammar-home';
import WorkbookTemplate from '@component/iyagigo/common/workbook-template';
import { useTypedDispatch, useTypedSelector } from '@hook/common/useStore';
import { actions as navigationActions } from '@store/slice/navigation-slice';
import { TIyagigoSubMenu } from '@datatype/iyagigo/type';
import { EApiLanguageCode } from '@datatype/iyagigo/api-type';

// ###################################
// 일본어 교실(classroom) = 왕초보 입문.
// 탭: 글자(character, 일본어) / 문법(grammar) / 기본표현(classroom-expression) / 생활회화(classroom-conversation).
// - 기본표현은 언어 요소(인사·숫자·시간), 생활회화는 장면(음식 주문·물건 구매) 축. 난이도 위계가 아닌 형제 탭.
// - 탭/템플릿(WorkbookTemplate)은 여기서 담당, body만 각 홈 컴포넌트로 분기.
// - 선택 탭은 navigation subMenu를 진실 공급원으로 사용.
// ###################################

type TClassroomTabId = NonNullable<TIyagigoSubMenu['classroom']>;

const JAPANESE_CLASSROOM_TABS: { id: TClassroomTabId; name: string }[] = [
  { id: 'character', name: '글자' },
  { id: 'grammar', name: '문법' },
  { id: 'expression', name: '기본표현' },
  { id: 'conversation', name: '생활회화' },
];

const ENGLISH_CLASSROOM_TABS: { id: TClassroomTabId; name: string }[] = [
  { id: 'grammar', name: '문법' },
  { id: 'expression', name: '기본표현' },
  { id: 'conversation', name: '생활회화' },
];

const ClassroomHome: React.FC<any> = () => {
  const dispatch = useTypedDispatch();
  const subMenu = useTypedSelector((state) => state.rootReducer?.navigationReducer?.iyagigo?.subMenu) ?? null;
  const studyLanguage =
    useTypedSelector((state) => state.rootReducer?.studentReducer?.currentStudyLanguage) ?? EApiLanguageCode.JAPANESE;
  const isEnglish = studyLanguage === EApiLanguageCode.ENGLISH;
  const classroomTabs = isEnglish ? ENGLISH_CLASSROOM_TABS : JAPANESE_CLASSROOM_TABS;

  // 영어 교실은 기본표현, 일본어 교실은 글자를 기본 탭으로 사용한다.
  const selectedTabId: TClassroomTabId = classroomTabs.some((tab) => tab.id === subMenu)
    ? (subMenu as TClassroomTabId)
    : isEnglish
      ? 'expression'
      : 'character';

  const handleTabClick = (id: TClassroomTabId) => {
    dispatch(navigationActions.storeIyagigoSubMenu({ mainMenu: 'classroom', subMenu: id }));
  };

  return (
    <WorkbookTemplate
      tabs={classroomTabs}
      selectedTabId={selectedTabId}
      onTabClick={(id) => handleTabClick(id as TClassroomTabId)}>
      {selectedTabId === 'character' && <CharacterHome />}
      {selectedTabId === 'expression' && <ClassroomExpressionHome />}
      {selectedTabId === 'conversation' && <ClassroomConversationHome />}
      {selectedTabId === 'grammar' && <GrammarHome />}
    </WorkbookTemplate>
  );
};

export default ClassroomHome;
